import { NetworkStatus, useQuery } from '@apollo/client';
import {
  IconButton,
  SelectionMode,
  Stack,
  Text
} from '@fluentui/react';
import { InfiniteList } from 'common/components/InfiniteList';
import { ColumnData } from 'common/components/SearchBar';
import { TABLE_ROWS } from 'common/constants';
import { useCommonStyles } from 'common/styles';
import { getSortedColumns } from 'common/utils/columnUtilities';
import { OrderDirection, SortOrder } from 'common/utils/commonTypes';
import { useCallback, useEffect, useRef, useState } from 'react';
import { WorkgroupGroupsView } from '../view';
import { DeleteWorkgroupGroup } from './DeleteWorkgroupGroup';
import WORKGROUPS_GROUPS from './WorkgroupGroups.graphql';
import {
  WorkgroupGroups,
  WorkgroupGroups_workgroupGroups_nodes,
  WorkgroupGroupsVariables,
} from './__generated__/WorkgroupGroups';
import { getColumns } from './column.data';
import { ColumnStylesProps, onRenderItems } from './columns.render';
import { useStyles } from './index.styles';
import { onHandleSearch } from './utils';

import { HelpIcon } from 'common/trainingSystem/HelpIcon';

export type WorkgroupGroupRow = WorkgroupGroups_workgroupGroups_nodes;

export const WorkgroupsGroupList = () => {
  const styles = useStyles();
  const commonStyles = useCommonStyles();
  const [selectedRows, setSelectedRows] = useState<WorkgroupGroupRow[]>([]);
  const [sortOrderParam, setSortOrderParam] = useState<SortOrder>();
  const [gridColumns, setGridColumns] = useState<ColumnData[]>([]);
  const renderRef = useRef(false);

  const columnStyles: ColumnStylesProps = {
    onRenderColumnStack: styles.onRenderColumnStack,
  };

  const { loading, data, networkStatus, refetch } = useQuery<
    WorkgroupGroups,
    WorkgroupGroupsVariables
  >(WORKGROUPS_GROUPS, {
    variables: {
      first: TABLE_ROWS,
      orderBy: ['NAME_ASC'],
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const refetching = loading && networkStatus !== NetworkStatus.fetchMore;

  const handleSearch = async () => {
    setSelectedRows([]);
    const { queryVariables } = onHandleSearch(sortOrderParam);
    refetch();
  };

  const handleSearchMemo = useCallback(handleSearch, [sortOrderParam]);

  const _onColumnClick = (clickedColumn: ColumnData) => {
    const { newColumns, desc } = getSortedColumns(clickedColumn, gridColumns);
    setGridColumns(newColumns);
    setSortOrderParam({
      column: clickedColumn.key,
      direction: desc ? OrderDirection.DESC : OrderDirection.ASC,
    });
  };

  const onRefresh = () => {
    refetch();
  };

  const { nodes } = { ...data?.workgroupGroups };

  const transformedData: WorkgroupGroupRow[] = refetching
    ? []
    : nodes?.map((item) => ({
        ...item,
      })) || [];

  const _renderItemColumn = useCallback(onRenderItems(columnStyles), [
    columnStyles,
  ]);

  const setCustomColumns = useCallback(() => {
    setGridColumns(getColumns());
  }, []);

  useEffect(() => {
    setCustomColumns();
  }, [setCustomColumns]);

  useEffect(() => {
    if (renderRef.current) handleSearchMemo();
    else renderRef.current = true;
  }, [handleSearchMemo]);

  return (
    <>
      <Stack
        tokens={{ childrenGap: 20 }}
        className={commonStyles.listHeaderContainer}
      >
        <Stack
          horizontal
          horizontalAlign="space-between"
          verticalAlign="center"
          className={commonStyles.listTitleContainer}
        >
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 7 }}>
            <Text variant="xLarge">Workgroup Groups</Text>
            <HelpIcon appLinkType="CORPORATE_WORKGROUPS" />
          </Stack>
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <IconButton
              iconProps={{ iconName: 'refresh' }}
              onClick={onRefresh}
            />
            <DeleteWorkgroupGroup selectedRows={selectedRows} />
            <WorkgroupGroupsView />
          </Stack>
        </Stack>
      </Stack>
      <InfiniteList
        compact={false}
        loading={loading}
        items={transformedData}
        selectionMode={SelectionMode.multiple}
        columns={gridColumns.filter((_column) => _column.isVisible)}
        onRenderItemColumn={_renderItemColumn}
        onColumnHeaderClick={(_, column) => {
          if (column) _onColumnClick(column);
        }}
        onSelectionChanged={setSelectedRows}
      />
    </>
  );
};
